import React, { useState, useRef, useEffect } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import { Paper, List, ListItemButton, ListItemText } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

const containerStyle = {
  width: '100%',
  height: '100%',
};

// 場所選択用のGoogleMapコンポーネント
export default function PlaceSelectMap({
  initialMarker,
  defaultCenter = { lat: 36.0, lng: 138.0 },
  onPlaceSelected, // 検索バー選択時に {lat, lng, name, address} を返す
  onMarkerChange, // クリック時に {lat, lng, placeId} を返す
  countryRestriction = 'JP',
}) {
  const [marker, setMarker] = useState(initialMarker || null);
  const [center, setCenter] = useState(initialMarker || defaultCenter);
  const [suggestions, setSuggestions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [activeIndex,setActiveIndex] = useState(-1);
  const mapRef = useRef(null);
  const autocompleteService = useRef(null);
  const sessionToken = useRef(null);
  const placesService = useRef(null);
  const debounceRef = useRef(null);

  // コンポーネントアンマウント時にタイマーをクリア
  useEffect(()=>{
    return ()=>{
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    }
  },[])

  // 親からinitialMarkerがnullや変更された場合に反映
  useEffect(() => {
    if (!initialMarker) {
      setMarker(null);
      setCenter(defaultCenter);
    } else {
      // 位置情報が変更された場合のみ反映
      if (!marker || initialMarker.lat !== marker.lat || initialMarker.lng !== marker.lng) {
        setMarker(initialMarker);
        setCenter(initialMarker);
      }
    }
  }, [initialMarker]);

  // 地図ロード
  const handleMapLoad = (map)=>{
    mapRef.current = map

    if (!autocompleteService.current) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
    if (!sessionToken.current) {
      sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
    }
    if (!placesService.current) {
      placesService.current = new window.google.maps.places.PlacesService(map);
    }
  }

  // 地図クリック
  // 地図クリック: markerのみ変更、centerはそのまま
  const handleMapClick = e => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarker({ lat, lng });
    if (onMarkerChange) onMarkerChange({ lat, lng, placeId: e.placeId || null });
  };

  // 入力に応じてサジェスト取得
  const handleInputChange = e => {
    const value = e.target.value;
    setInputValue(value);

    // 前のタイマーをキャンセル
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    // サジェスト取得タイミングを0.5秒遅らせる（debounce）
    debounceRef.current = setTimeout(() => {
      // サービスとトークンと入力がない場合はサジェストクリア
      if (!autocompleteService.current || !sessionToken.current || !value) {
        setSuggestions([]);
        return;
      }
      // サジェスト取得
      autocompleteService.current.getPlacePredictions(
        { input: value, componentRestrictions: countryRestriction ? { country: countryRestriction } : {}, sessionToken: sessionToken.current },
        preds => {
          setSuggestions(Array.isArray(preds) ? preds.slice(0, 10) : []);
        }
      );
    }, 500);
  };

  // サジェスト選択: markerとcenter両方変更
  const handleSelectSuggestion = async pred => {
    //setInputValue(pred.description);
    setInputValue(pred.structured_formatting.main_text);
    setSuggestions([]);
    setActiveIndex(-1)

    // 位置情報取得
    if (!placesService.current) return;
    placesService.current.getDetails(
      { placeId: pred.place_id, fields: ['geometry', 'name', 'formatted_address'], sessionToken: sessionToken.current }, 
      place => {
        if (!place || !place.geometry) return;
        const pos = { 
          lat: place.geometry.location.lat(), 
          lng: place.geometry.location.lng() 
        };
        // マーカーと中心を更新
        setMarker(pos);
        setCenter(pos);
        // 選択情報を親に渡す
        if (onPlaceSelected) {
          onPlaceSelected({ 
            lat: pos.lat, 
            lng: pos.lng, 
            //name: place.name, 
            name: pred.structured_formatting.main_text, // フォーマットされたテキストを優先 nameは新宿３丁目が３丁目と返ってくるから
            address: place.formatted_address });
        }
        // 次の検索のために token を更新
        sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();      
    });
  };

  // 検索バー入力クリアボタン
  const handleInputClear = () => {
    setInputValue('');
    setSuggestions([]);
    setActiveIndex(-1)
    // 入力のみクリアで位置は削除しない
    //setMarker(null);
    //setCenter(defaultCenter);
    //if (onMarkerChange) onMarkerChange(null);
  };

  // サジェストリストのキーボード操作: 上下で選択、Enterで決定
  const handleSuggestKeyDown = e => {
    if(!suggestions.length) return;

    if(e.key==="ArrowDown"){
      e.preventDefault()
      setActiveIndex(prev=>prev<suggestions.length-1?prev+1:prev)
    }
    if(e.key==="ArrowUp"){
      e.preventDefault()
      setActiveIndex(prev=>prev>0?prev-1:0)
    }
    if(e.key==="Enter"){
      if(activeIndex>=0){
        e.preventDefault()
        handleSelectSuggestion(suggestions[activeIndex])
      }
    }
    if(e.key==="Escape"){
      e.preventDefault()
      e.stopPropagation()
      setSuggestions([])
      setActiveIndex(-1)
    }
  };

  // 地図と検索バーを重ねて表示
  return (
    <div style={{ position: 'relative', height: '100%' }}>
      {/* MUI検索バー */}
      <div style={{ position: 'absolute', zIndex: 10, left: 220, top: 10, minWidth: 400 }}>
        <TextField
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleSuggestKeyDown}
          placeholder="場所を検索..."
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              inputValue && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleInputClear}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            ),
            style: { backgroundColor: '#fff', fontSize: 14 },
          }}
        />
        {/* サジェストリスト */}
        {suggestions.length > 0 && (
          <Paper
            sx={{
            mt:0.5,
            maxHeight:320,
            overflow:"auto"
            }}
          >
            <List dense>
              {suggestions.map((s,index)=>(
              <ListItemButton
                key={s.place_id}
                selected={index===activeIndex}
                onMouseEnter={()=>setActiveIndex(index)}
                onClick={()=>handleSelectSuggestion(s)}
              >
                <ListItemText
                  primary={s.structured_formatting.main_text}
                  secondary={s.structured_formatting.secondary_text}
                  primaryTypographyProps={{fontSize:14}}
                  secondaryTypographyProps={{fontSize:12}}
                />
              </ListItemButton>
              ))}
            </List>
          </Paper>
        )}
      </div>
      {/* 地図 */}
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={15}
        onLoad={handleMapLoad}
        onClick={handleMapClick}
        options={{
          clickableIcons: true,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,

          draggableCursor: 'default', // 通常時は矢印
          draggingCursor: 'grab',     // ドラッグ中は移動カーソル
        }}
      >
        {marker && <Marker position={marker} />}
      </GoogleMap>
    </div>
  );
}